import React, { useEffect, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import axios from "axios";

type AvaliacaoDTO = {
  id: number;
  nota: number;
  comentario?: string;
  usuarioNome?: string;
  usuarioFotoUrl?: string;
  fotosPorPonto?: { fotoId: number; ponto: string; url: string }[];
  fotos?: number[];
};

type Props = {
  motoristaId: number;
  open: boolean;
  onClose: () => void;
  baseUrl: string;
};

const DriverReviewsModal: React.FC<Props> = ({ motoristaId, open, onClose, baseUrl }) => {
  const [avaliacoes, setAvaliacoes] = useState<AvaliacaoDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSlides, setLightboxSlides] = useState<{ src: string }[]>([]);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  // Buscar avaliações
  useEffect(() => {
    if (!open) return;

    const fetchAvaliacoes = async () => {
      setLoading(true);
      try {
        const res = await axios.get<AvaliacaoDTO[]>(`${baseUrl}/avaliacoes/motorista/${motoristaId}`, { headers });
        setAvaliacoes(res.data);
      } catch (error) {
        console.error("Erro ao buscar avaliações:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAvaliacoes();
  }, [open, motoristaId, baseUrl]);

  const calcularMedia = (avaliacoes: AvaliacaoDTO[]) => {
    if (!avaliacoes || avaliacoes.length === 0) return 0;
    return avaliacoes.reduce((sum, a) => sum + (a.nota || 0), 0) / avaliacoes.length;
  };

  const media = calcularMedia(avaliacoes);

  const openLightbox = (slides: { src: string }[]) => {
    setLightboxSlides(slides);
    setLightboxOpen(true);
  };

  return (
    <>
      {/* Modal */}
      <div className={`modal fade ${open ? "show d-block" : ""}`} tabIndex={-1} aria-hidden={!open}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Motorista Reviews</h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              {loading ? (
                <p>Loading reviews...</p>
              ) : avaliacoes.length === 0 ? (
                <p>No reviews yet.</p>
              ) : (
                <>
                  <p>
                    <strong>Average Rating:</strong>{" "}
                    <span className="badge badge-warning p-1 text-black">
                      <i className="isax isax-star" /> {media.toFixed(1)} ({avaliacoes.length} reviews)
                    </span>
                  </p>

                  {avaliacoes.map((av) => (
                    <div key={av.id} className="card mb-2 p-2">
                      <div className="d-flex align-items-center mb-2">
                        <img
                          src={av.usuarioFotoUrl ? `${baseUrl}${av.usuarioFotoUrl}` : "/assets/img/users/default-avatar.png"}
                          className="avatar rounded-circle me-2"
                          alt={av.usuarioNome}
                          style={{ width: "40px", height: "40px" }}
                        />
                        <div>
                          <strong>{av.usuarioNome}</strong>
                          <span className="badge badge-warning p-1 ms-2 text-black"><i className="isax isax-star"/> {av.nota.toFixed(1)}</span>
                        </div>
                      </div>
                      {av.comentario && <p style={{ fontSize: "0.9rem", lineHeight: 1.4 }}>{av.comentario}</p>}

                      {/* Fotos */}
                      {(av.fotosPorPonto?.length || 0) > 0 || (av.fotos?.length || 0) > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {av.fotosPorPonto?.map((f) => (
                            <div
                              key={`ponto-${f.fotoId}`}
                              style={{ width: "80px", height: "80px", cursor: "pointer", position: "relative" }}
                              onClick={() =>
                                openLightbox([
                                  ...(av.fotos?.map((fid) => ({ src: `${baseUrl}/uploads/${fid}` })) || []),
                                  ...(av.fotosPorPonto?.map((fp) => ({ src: `${baseUrl}${fp.url}` })) || []),
                                ])
                              }
                            >
                              <img
                                src={`${baseUrl}${f.url}`}
                                alt={f.ponto || "foto"}
                                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "6px" }}
                              />
                              <div
                                style={{
                                  position: "absolute",
                                  bottom: 0,
                                  left: 0,
                                  width: "100%",
                                  background: "rgba(0,0,0,0.5)",
                                  color: "#fff",
                                  fontSize: "0.7rem",
                                  textAlign: "center",
                                  borderBottomLeftRadius: "6px",
                                  borderBottomRightRadius: "6px",
                                }}
                              >
                                {f.ponto}
                              </div>
                            </div>
                          ))}
                          {av.fotos?.map((fid) => (
                            <div
                              key={`foto-${fid}`}
                              style={{ width: "80px", height: "80px", cursor: "pointer" }}
                              onClick={() =>
                                openLightbox([
                                  ...(av.fotos?.map((fid2) => ({ src: `${baseUrl}/uploads/${fid2}` })) || []),
                                  ...(av.fotosPorPonto?.map((fp) => ({ src: `${baseUrl}${fp.url}` })) || []),
                                ])
                              }
                            >
                              <img
                                src={`${baseUrl}/uploads/${fid}`}
                                alt="foto"
                                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "6px" }}
                              />
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop */}
      {open && <div className="modal-backdrop fade show"></div>}

      {/* Lightbox */}
      <Lightbox open={lightboxOpen} close={() => setLightboxOpen(false)} slides={lightboxSlides} />
    </>
  );
};

export default DriverReviewsModal;
